﻿//==========================================================================================================
// Copyright (C) Nore Technology AB. All Rights Reserved.
//==========================================================================================================

//-------------------------------------------------------------------------------------
// Imports
//-------------------------------------------------------------------------------------

import * as React from "react";

import { BrowserRouter, Route, Routes } from "react-router-dom";

import LoginPageCustomization from "~jrad/Pages/Account/Login";
import { useSettings } from "~base/Settings/SettingsContext";

import { ProtectedRoute } from "~base/Pages/Shared/ProtectedRoute";
import { LoginPage } from "~base/Pages/Account/Login";
import { LogoutPage } from "~base/Pages/Account/Logout";
import { ResetPage } from "~base/Pages/Account/Reset";
import { ExternalApiCodePage } from "~base/Pages/Account/ExternalApiCode";
import { ConfirmPage } from "~base/Pages/Account/Confirm";
import { ConfirmEmailPage } from "~base/Pages/Account/ConfirmEmail";
import { AccessDeniedPage } from "~base/Pages/Account/AccessDenied";
import { NotFoundPage } from "~base/Pages/Home/NotFound";
import { ResetCodePage } from "~base/Pages/Account/ResetCode";
import { SetPasswordPage } from "~base/Pages/Account/SetPassword";
import UppAppDownloadPage from "~jrad/Pages/UppApp";
import SelfServicePage from "~jrad/Pages/SelfService";
import { ReloadPage } from "~base/Pages/Shared/Reload";

import { RegisterPage } from "~base/Pages/Account/Register";
import RegisterPageCustomization from "~jrad/Pages/Account/Register";
import { StartPageSwitch } from "~jrad/Pages/Home/Start/StartPageSwitch";
import CustomerSettingsPage from "~jrad/Pages/Home/CustomerSettings";
import type { PageRouteInfo } from "~base/Pages/Shared/PageRoute";
import { createPageRoute } from "~base/Pages/Shared/PageRoute";
import SelfServiceLoginPage from "./Pages/SelfServiceLogin";

//-------------------------------------------------------------------------------------
// Props
//-------------------------------------------------------------------------------------

type MainRouterProps = {
    children?: React.ReactNode;
};

//-------------------------------------------------------------------------------------
// Routes
//-------------------------------------------------------------------------------------

const account: PageRouteInfo[] = [
    {
        path: "Account/Login/*",
        component: <LoginPage customizations={LoginPageCustomization} />,
    },
    { path: "Account/ExternalApiCode", component: <ExternalApiCodePage /> },
    { path: "Account/Reset/*", component: <ResetPage /> },
    { path: "Account/Reset/*", component: <ResetPage /> },
    { path: "Account/ResetCode/*", component: <ResetCodePage /> },
    { path: "Account/SetPassword/*", component: <SetPasswordPage /> },
    {
        path: "Account/Register/*",
        component: <RegisterPage customizations={RegisterPageCustomization} />,
    },
    { path: "Account/Confirm/*", component: <ConfirmPage /> },
    { path: "Account/ConfirmEmail/*", component: <ConfirmEmailPage /> },
    { path: "Account/AccessDenied/*", component: <AccessDeniedPage /> },
    { path: `Account/Forbidden/*`, component: <AccessDeniedPage /> },
    { path: "Account/Logout/*", component: <LogoutPage /> },
];

const admin: PageRouteInfo[] = [
    {
        path: "/Admin/*",
        component: (
            <ProtectedRoute>
                <ReloadPage />
            </ProtectedRoute>
        ),
    },
    {
        path: "/SoPNLAdmin/*",
        component: (
            <ProtectedRoute>
                <ReloadPage />
            </ProtectedRoute>
        ),
    },
];

const home: PageRouteInfo[] = [
    {
        path: "Home/Start/*",
        component: (
            <ProtectedRoute>
                <StartPageSwitch />
            </ProtectedRoute>
        ),
    },
    {
        path: "Home/Settings",
        component: (
            <ProtectedRoute>
                <CustomerSettingsPage />
            </ProtectedRoute>
        ),
    },
    {
        path: "/",
        component: (
            <ProtectedRoute>
                <StartPageSwitch />
            </ProtectedRoute>
        ),
    },
    { path: "/*", component: <NotFoundPage /> },
];

//-------------------------------------------------------------------------------------
// Component
//-------------------------------------------------------------------------------------

const MainRouter: React.FunctionComponent<MainRouterProps> = (
    props: MainRouterProps
) => {
    const settings = useSettings();

    const customer: PageRouteInfo[] = [
        {
            path: settings.valueAsString("DataGatheringAppVirtualPath"),
            component: <UppAppDownloadPage />,
        },
        {
            path: settings.valueAsString(
                "selfService.virtualPath.registration"
            ),
            component: <SelfServicePage />,
        },
        {
            path:
                settings.valueAsString(
                    "selfService.virtualPath.anonymousLogin"
                ) + "/*",
            component: <SelfServiceLoginPage />,
        },
    ];

    return (
        <BrowserRouter>
            <Routes>
                {account.map((x) => createPageRoute(x))}
                {customer.map((x) => createPageRoute(x))}
                {admin.map((x) => createPageRoute(x))}
                {home.map((x) => createPageRoute(x))}
            </Routes>
        </BrowserRouter>
    );
};

//-------------------------------------------------------------------------------------
// Exports
//-------------------------------------------------------------------------------------

export default MainRouter;
